// File: ProfileForm.js
import React from 'react'
import { Form, Input, DatePicker, Select, Upload, message, Col, Row, InputNumber } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import CountryStateCity from 'src/Components/CountryStateCity'
import useMentorProfile from 'src/Hooks/MentorHooks/useMentorProfile'
import 'src/Pages/LibraryPages/librarypages.scss'

const { Option } = Select

const ProfileForm = () => {
  const {
    form,
    previewImage,
    loading,
    loader,
    handleSubmit,
    handleExpertiseChange,
    showSkillInputs,
    skillsList,
    setPreviewImage,
  } = useMentorProfile()

  return (
    <div className="lpg lpg--compact">
      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 21a8 8 0 0 0-16 0" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <div>
            <h1 className="lpg__title">My Profile</h1>
            <p className="lpg__sub">View and update your profile.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : (
        <div className="lpg__panel-box">
          <Form
            form={form}
            layout="vertical"
            size="small"
            onFinish={handleSubmit}
            initialValues={{
              mentorSkills: [],
            }}
            encType="multipart/form-data"
          >
            <Form.Item label="Profile Image" name="profileImageFile" valuePropName="file">
              <Upload
                name="profileImageFile"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={(file) => {
                  if (!file.type.startsWith('image/')) {
                    message.error('Please select an image file')
                    return false
                  }
                  if (file.size > 5 * 1024 * 1024) {
                    message.error('File size must be less than 5MB')
                    return false
                  }
                  return true
                }}
                onChange={({ file }) => {
                  if (file.status === 'done') {
                    setPreviewImage(URL.createObjectURL(file.originFileObj))
                  } else if (file.status === 'error') {
                    message.error(`${file.name} file upload failed.`)
                  }
                  form.setFieldsValue({
                    profileImageFile: file.status === 'removed' ? null : file,
                  })
                }}
                customRequest={({ file, onSuccess }) => {
                  setTimeout(() => onSuccess('ok'), 0)
                }}
              >
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="Selected Avatar"
                    style={{
                      height: '100px',
                      width: '100px',
                      cursor: 'pointer',
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  <div>
                    <UploadOutlined />
                    <div>Click to upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>

            <Row gutter={16}>
              {/* Basic Info Fields */}
              <Col span={6}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: 'First Name is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Middle Name" name="middleName">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: 'Last Name is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="Email" name="email">
                  <Input readOnly />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="Date of Birth"
                  name="dateOfBirth"
                  rules={[{ required: true, message: 'Date of Birth is required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="Gender"
                  name="gender"
                  rules={[{ required: true, message: 'Gender is required' }]}
                >
                  <Select placeholder="Select Gender">
                    <Option value="Male">Male</Option>
                    <Option value="Female">Female</Option>
                    <Option value="UnSpecified">UnSpecified</Option>
                    <Option value="Do not wish to disclosed">Do not wish to disclosed</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="Expertise Area"
                  name="expertiseArea"
                  rules={[{ required: true, message: 'Expertise Area is required' }]}
                >
                  <Select placeholder="Select Expertise Area" onChange={handleExpertiseChange}>
                    <Option value={1}>Data Analysis</Option>
                    <Option value={2}>Data Modeling</Option>
                    <Option value={3}>Content Creation</Option>
                    <Option value={4}>Go to Market</Option>
                    <Option value={5}>Design Elements</Option>
                  </Select>
                </Form.Item>
              </Col>
              <CountryStateCity form={form} span={6} />

              {showSkillInputs && (
                <>
                  <Col span={6}>
                    <Form.Item
                      label="Year of Experience"
                      name="yearsOfExperience"
                      rules={[{ required: true, message: 'Years of experience is required' }]}
                    >
                      <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Mentor Experience Level"
                      name="mentorshipExperienceLevel"
                      rules={[{ required: true, message: 'Experience level is required' }]}
                    >
                      <Select
                        placeholder="Select experience level"
                        options={[
                          { value: 1, label: 'Beginner' },
                          { value: 2, label: 'Intermediate' },
                          { value: 3, label: 'Advanced' },
                          { value: 4, label: 'Expert' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Technical Skills"
                      name="mentorSkills"
                      rules={[{ required: true, message: 'Please select at least one skill' }]}
                    >
                      <Select mode="multiple" placeholder="Select technical skills">
                        {skillsList.map((skill) => (
                          <Option key={skill.id} value={skill.name}>
                            {skill.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            <Form.Item>
              <button type="submit" className="lpg__btn-primary" disabled={loader}>
                {loader ? 'Please Wait...' : 'Submit'}
              </button>
            </Form.Item>
          </Form>
        </div>
      )}
    </div>
  )
}

export default ProfileForm
